<template>
    <primevue-dialog
        :class="{ 'no-visible-footer': !visibleModalHasFooter }"
        :style="size"
        dismissable-mask
        modal
        :draggable="false"
        close-on-escape
        :visible="centralModalStore.modalVisible"
        @update:visible="handleOuterDialogVisibleUpdate"
        @hide="handleOuterDialogHide"
    >
        <template #header>
            <div class="breadcrumb-container" v-if="centralModalStore.breadcrumbItems.length > 1">
                <breadcrumb-with-underlining
                    :breadcrumb-items="centralModalStore.breadcrumbItems"
                />
                <action-button
                    title="Go back"
                    :icon="['fas', 'reply']"
                    @click="goBackOneStep"
                />
            </div>
            <div class="modal-header-text" v-else-if="!centralModalStore.currentVisibleModal!.hideHeader">{{ centralModalStore.breadcrumbItems[0].label }}</div>
            <template v-for="(modal) in centralModalStore.modalStack" :key="modal.guid">
                <div v-show="centralModalStore.currentVisibleModal?.guid === modal.guid" :ref="el => setHeaderRef(modal.guid, el)" :style="headerStyle" :id="`modal-header-root-${ modal.guid }`" alive />
            </template>
        </template>
        <template v-for="(modal) in centralModalStore.modalStack" :key="modal.guid">
            <div
                v-show="centralModalStore.currentVisibleModal?.guid === modal.guid"
                class="modal-content"
                :id="`modal-root-${ modal.guid }`"
            >
                <span
                    v-if="centralModalStore.currentVisibleModal?.guid === modal.guid"
                    :ref="(el) => setModalContentFocusSentinelRef(el)"
                    class="modal-content-focus-sentinel p-hidden-accessible"
                    tabindex="-1"
                    @focus="() => applyModalContentFocus(modal.guid)"
                />
            </div>
        </template>
        <template v-if="centralModalStore.modalStack.length > 0 && centralModalStore.modalStack.some(x => x.hasFooter)" #footer>
            <template v-for="(modal) in centralModalStore.modalStack" :key="modal.guid">
                <div v-show="centralModalStore.currentVisibleModal?.guid === modal.guid" class="modal-footer" :id="`modal-footer-root-${ modal.guid }`" alive />
            </template>
        </template>
    </primevue-dialog>
    <confirm-dialog group="central-modal">
        <template #container="{ message, acceptCallback, rejectCallback }">
            <div class="confirm-dialog-wrapper">
                <div class="confirm-dialog-message">
                    {{ message }}
                </div>
                <div class="flex-row-with-gap move-to-right">
                    <primevue-button text @click="acceptCallback" class="plain-btn">
                        Yes
                        <font-awesome-icon :icon="['fas', 'check']" />
                    </primevue-button>
                    <primevue-button text @click="rejectCallback" class="plain-btn">
                        No
                        <font-awesome-icon :icon="['fas', 'xmark']" />
                    </primevue-button>
                </div>
            </div>
        </template>
    </confirm-dialog>
</template>

<script lang="ts" setup>
import {
    watch, nextTick, computed, ref,
} from 'vue';
import type { ComponentPublicInstance } from 'vue';
import { useCentralModalStore } from '@/stores/centralModalStore.ts';
import PrimevueDialog from 'primevue/dialog';
import ConfirmDialog from 'primevue/confirmdialog';
import BreadcrumbWithUnderlining from '@/components/common/BreadcrumbWithUnderlining.vue';
import ActionButton from '@/components/powerMarkets/common/ActionButton.vue';
import PrimevueButton from 'primevue/button';

const centralModalStore = useCentralModalStore();

const MODAL_CONTENT_FOCUSABLE_SELECTOR = 'input:not([disabled]), button:not([disabled]), textarea:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"]):not(.modal-content-focus-sentinel)';

function findFirstFocusableInModalContent (root: HTMLElement): HTMLElement | null {
    const candidates = root.querySelectorAll<HTMLElement>(MODAL_CONTENT_FOCUSABLE_SELECTOR);
    const match = Array.from(candidates).find((el) => !el.classList.contains('modal-content-focus-sentinel'));
    return match ?? null;
}

function focusDialogCloseButton (root: HTMLElement): void {
    root.closest('.p-dialog')?.querySelector<HTMLElement>('.p-dialog-close-button')?.focus();
}

async function applyModalContentFocus (guid: string): Promise<void> {
    const root = document.getElementById(`modal-root-${guid}`);
    if (!root) return;

    let focusable = findFirstFocusableInModalContent(root);
    if (!focusable) {
        await nextTick();
        focusable = findFirstFocusableInModalContent(root);
    }

    if (focusable) {
        focusable.focus({ preventScroll: true });
        return;
    }

    focusDialogCloseButton(root);
}

function setModalContentFocusSentinelRef (el: Element | ComponentPublicInstance | null): void {
    const node = el as HTMLElement | null;
    node?.setAttribute('autofocus', '');
}

function focusVisibleModalContentSentinel (guid: string): void {
    const root = document.getElementById(`modal-root-${guid}`);
    const sentinel = root?.querySelector<HTMLElement>('.modal-content-focus-sentinel');
    sentinel?.focus();
}

function handleOuterDialogVisibleUpdate (nextVisible: boolean): void {
    if (nextVisible) {
        centralModalStore.modalVisible = nextVisible;
        return;
    }

    if (!centralModalStore.shouldAllowClosingModalStack()) {
        centralModalStore.modalVisible = true;
        return;
    }
    centralModalStore.closeAllModals();
}

function handleOuterDialogHide (): void {
    if (!centralModalStore.modalVisible) return;
    handleOuterDialogVisibleUpdate(false);
}

const headerRefs = ref<Record<string, HTMLElement | null>>({});

function setHeaderRef (guid: string, el: Element | ComponentPublicInstance | null): void {
    const node = el as HTMLElement | null;
    if (node) {
        headerRefs.value[guid] = node;
    } else {
        delete headerRefs.value[guid];
    }
}

const visibleModalHasFooter = computed(() => (centralModalStore.currentVisibleModal?.hasFooter ?? false));

function goBackOneStep (): void {
    if (!centralModalStore.shouldAllowClosingTopModal()) return;
    centralModalStore.modalStack.length -= 1;
}

const headerStyle = computed(() => {
    const guid = centralModalStore.currentVisibleModal?.guid;
    if (!guid) return {};
    const el = headerRefs.value[guid] ?? null;
    if (!el) return {};
    if (!el.childNodes) return {};
    return { width: '100%' };
});

const size = computed<{width?: string; height?: string} | null>(() => {
    if (!centralModalStore.currentVisibleModal) return null;

    switch (centralModalStore.currentVisibleModal.size) {
        case 'small': return { width: '50vw', height: '50vh' };
        case 'medium': return { width: '80vw', height: '80vh' };
        case 'large': return { width: '90vw', height: '90vh' };
        case 'extra-large': return { width: '95vw', height: '95vh' };
        default: {
            if (typeof centralModalStore.currentVisibleModal.size === 'object') {
                return {
                    width: centralModalStore.currentVisibleModal.size.width,
                    height: centralModalStore.currentVisibleModal.size.height,
                };
            }
            return null;
        }
    }
});

watch(() => centralModalStore.currentVisibleModal?.guid, (guid, prevGuid) => {
    if (!guid || !centralModalStore.modalVisible) return;
    if (prevGuid === undefined) return;

    nextTick(() => {
        focusVisibleModalContentSentinel(guid);
    });
});

watch(() => centralModalStore.modalStack.length, () => {
    nextTick(() => {
        const content = document.querySelector('.p-dialog-content');
        if (content) {
            content.scrollTop = 0;
        }
    });
});

</script>

<style lang="scss" scoped>

.breadcrumb-container {
    display: flex;
    align-items: center;
    width: 100%;
    overflow: hidden;

    @include responsive(sm) {
        overflow: unset;
    }
}

.modal-content {
    height: 100%;
    overflow-y: auto;
}
.modal-header-text {
    white-space: nowrap;
}
.modal-footer {
    display: flex;
    justify-content: end;
    width: 100%;
}
</style>
